import React from "react";
import axios from "axios";
import { Table } from "react-bootstrap";
import { useEffect, useState, useCallback, useMemo } from "react";
import GLOBALVAR from "../../constants";
import { format } from 'date-fns';
import LoadingSpinner from "../../components/misc/LoadingSpinner";
import './css/ListPaginationApplicatiion.scss'; 
import ViewportChecker from "./ViewportChecker";
import debounce from 'lodash/debounce';

function ListPaginationApplication() {
    const [applicationHeaders, setApplicationHeaders] = useState([]);
    const [applicationLists, setApplicationLists] = useState([]);
    const [showSpinner, setShowSpinner] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [count, setCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const itemsPerPage = 10;
    const isMobile = ViewportChecker();

    const fetchDataApplications = useCallback(async (currentPage, searchQuery = "") => {
        setShowSpinner(true);

        try {
            let response;
            if (searchQuery) {
                response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/applicationsearch`, {
                    params: {
                        query: searchQuery,
                        page: currentPage,
                        limit: itemsPerPage
                    }
                });
            } else {
                response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/applications/fetch/all?page=${currentPage}&limit=${itemsPerPage}`);
            }

            const appdata = response.data.rows.reduce((acc, item) => {
                let formattedBirthday = '';
                try {
                    if (item.birthday) {
                        const date = new Date(item.birthday);
                        if (!isNaN(date.getTime())) {
                            formattedBirthday = format(date, 'MM/dd/yyyy');
                        }
                    }
                } catch (error) {
                    console.warn('Invalid date format for birthday:', item.birthday);
                }

                acc[item.id] = {
                    items: [
                        { key: 'id', value: item.id },
                        { key: 'fullname', value: `${item.lastName}, ${item.firstName} ${item.middleName}` },
                        { key: 'birthday', value: formattedBirthday },
                        { key: 'mobileno', value: item.mobileNo1 },
                        { key: 'email', value: item.emailAddress },
                        { key: 'address', value: `${item.street} ${item.barangay}, ${item.municipality}, ${item.province} ${item.zipCode}` },
                        { key: 'action', value: item.id }
                    ]
                };
                return acc;
            }, {});

            setCount(response.data.count);
            setApplicationLists(appdata);

            const applicationhead = [
                { value: "ID" },
                { value: "Full Name" },
                { value: "Birthday" },
                { value: "Mobile #" },
                { value: "Email" },
                { value: "Address" },
                { value: "Action" }
            ];

            setApplicationHeaders(applicationhead);
            setShowSpinner(false);
            setIsSearching(false);
        } catch (error) {
            console.error('Error submitting form:', error);
            setShowSpinner(false);
            setIsSearching(false);
        }
    }, [itemsPerPage]); // Only depends on itemsPerPage which is constant

    // Debounced search function
    const debouncedSearch = useCallback(
        (query) => {
            setCurrentPage(1);
            fetchDataApplications(1, query);
        },
        [fetchDataApplications]
    );

    const debouncedSearchWithDelay = useMemo(
        () => debounce(debouncedSearch, 500),
        [debouncedSearch]
    );

    const handleSearchChange = (event) => {
        const query = event.target.value;
        setSearchQuery(query);
        setIsSearching(true);
        debouncedSearchWithDelay(query);
    };

    const handleSearchSubmit = (event) => {
        event.preventDefault();
        setCurrentPage(1);
        fetchDataApplications(1, searchQuery);
    };

    const btnPrev = () => {
        setCurrentPage(prevPage => prevPage - 1);
    };

    const btnNext = () => {
        setCurrentPage(prevPage => prevPage + 1);
    };

    const btnPage = (page) => {
        setCurrentPage(page);
    };

    const btnFirst = () => {
        setCurrentPage(1);
    };

    const btnLast = () => {
        const totalPages = Math.ceil(count / itemsPerPage);
        setCurrentPage(totalPages);
    };

    const totalPages = Math.ceil(count / itemsPerPage);

    const getPageNumbers = () => {
        const pageNumbers = [];
        const totalPagesToShow = 3;

        if (totalPages <= totalPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            const lastPage = currentPage + 1 > totalPages ? totalPages : currentPage + 1;
            const firstPage = lastPage - (totalPagesToShow - 1) > 0 ? lastPage - (totalPagesToShow - 1) : 1;
            const dots = currentPage + 1 < totalPages ? '...' : '';

            for (let i = firstPage; i <= lastPage; i++) {
                pageNumbers.push(i);
            }

            if (dots) {
                pageNumbers.push(dots);
            }

            pageNumbers.push(totalPages);
        }

        return pageNumbers;
    };

    const focussearch = () => {
        const inputElement = document.querySelector('.search-input-applicant');
        inputElement.focus();
    };

    useEffect(() => {
        fetchDataApplications(currentPage, searchQuery);
    }, [currentPage, searchQuery, fetchDataApplications]); // Added fetchDataApplications to dependencies

    return (
        <>
        {showSpinner && (<LoadingSpinner />)}
        <div className="list-wrapper">
            <div className="list-header">
                <div className="list-title">
                    {!isMobile && <h1>Applicant List</h1>}
                    <div className="search-wrapper">
                        <form onSubmit={handleSearchSubmit} className="searchform" onClick={focussearch}>
                            <div className="search-field">
                                <div className="search-icon">
                                    <i className="fa-solid fa-magnifying-glass"></i>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search by name, email, or mobile number..."
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    className={`search-input-applicant ${isSearching ? 'searching' : ''}`}
                                />
                                {searchQuery && (
                                    <button 
                                        type="button" 
                                        className="search-clear" 
                                        onClick={() => {
                                            setSearchQuery('');
                                            setIsSearching(false);
                                            debouncedSearch('');
                                        }}
                                    >
                                        <i className="fa-solid fa-xmark"></i>
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div className="table-responsive">
                <Table className="list-container list-applicants" hover>
                    <thead>
                        <tr>
                            {applicationHeaders.map((item, index) => (
                                <th className="list-header-item" key={index}>
                                    {item.value}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {Object.values(applicationLists).reverse().map((item, index) => (
                            <tr className="list-body" key={index}>
                                {item.items.map((subitem, subindex) => {
                                    let itemval = subitem.value;

                                    if (subitem.key === "id") {
                                        itemval = (
                                            <a href={`/applicant/${subitem.value}`} className="id-link">
                                                {subitem.value.toString().padStart(6, '0')}
                                            </a>
                                        );
                                    }

                                    if (subitem.key === "action") {
                                        itemval = (
                                            <div className="action-buttons">
                                                <a 
                                                    className="btn-action view" 
                                                    href={`/applicant/${subitem.value}`}
                                                    title="View Details"
                                                >
                                                    <i className="fa-solid fa-magnifying-glass"></i>
                                                </a>
                                                <a 
                                                    className="btn-action add" 
                                                    href={`/loan-processing/?id=${subitem.value}`}
                                                    title="Add Loan"
                                                >
                                                    <i className="fa-solid fa-square-plus"></i>
                                                </a>
                                            </div>
                                        );
                                    }

                                    return (
                                        <td className="list-body-item" key={`${index}+${subindex}`}>
                                            {itemval}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="page-buttons">
                            <td colSpan="7">
                                <div className="pagination-wrapper">
                                    <div className="page-info">
                                        Showing {applicationLists.length} of {count} entries
                                    </div>
                                    <div className="page-buttons__container">
                                        <button onClick={btnFirst} disabled={currentPage === 1}>
                                            <i className="fa-solid fa-angles-left"></i>
                                        </button>
                                        <button onClick={btnPrev} disabled={currentPage === 1}>
                                            <i className="fa-solid fa-angle-left"></i>
                                        </button>
                                        {getPageNumbers().map((page, index) => (
                                            <React.Fragment key={index}>
                                                {page === '...' ? (
                                                    <span className="elip">
                                                        <i className="fa-solid fa-ellipsis"></i>
                                                    </span>
                                                ) : (
                                                    <span className="number">
                                                        <button onClick={() => btnPage(page)} disabled={currentPage === page}>
                                                            {page}
                                                        </button>
                                                    </span>
                                                )}
                                            </React.Fragment>
                                        ))}
                                        <button onClick={btnNext} disabled={currentPage === totalPages}>
                                            <i className="fa-solid fa-angle-right"></i>
                                        </button>
                                        <button onClick={btnLast} disabled={currentPage === totalPages}>
                                            <i className="fa-solid fa-angles-right"></i>
                                        </button>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
        </>
    );
}

export default ListPaginationApplication;